import React, { memo, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { User } from "../../../interfaces/entities/user";
import { Branch } from "../../../interfaces/entities/branch";

import { Collection } from "../../../enums/collections.enum";
import Button from "@mui/material/Button";
import { ReviewContainer } from "../employeeReview/employeeReviewContainer";
import { Card, CardActionArea, CardActions, CardContent, CardMedia, Typography } from "@mui/material";
import { useAuthenticatedUser } from "../../../hooks/useAuthenticatedUser";

interface EmployeeViewProps {
  userData: User;
  branch?: Branch;
  roleLabel: string;
}

// Memoized card view component for better list rendering performance
// Prevents unnecessary re-renders when parent list updates
export const EmployeeCardView: React.FC<EmployeeViewProps> = memo(({ userData, branch, roleLabel }) => {

  const { currentUserIsOwnerOf } = useAuthenticatedUser();
  const [reviewDialogOpen, setReviewDialogOpen] = useState(false);

  // Memoize the card style to prevent style recalculation
  const cardStyle = useMemo(() => ({
    maxWidth: { sm: '100%', md: '250px' } as const,
    backgroundColor: '#FFFFFF50'
  }), []);

  // Memoize the card media image source
  const imageSrc = useMemo(() => userData.image || undefined, [userData.image]);

  // Memoize the view link target
  const viewLinkTarget = useMemo(() => 
    `${Collection.Users}/${userData.id}`, 
    [userData.id]
  );

  return (
    <Card sx={cardStyle}>
      <CardActionArea>
        <CardMedia
          component="img"
          height="250"
          image={imageSrc}
          alt={`${userData.firstName} ${userData.lastName}`}
          onClick={() => setReviewDialogOpen(true)}
          sx={{ cursor: 'pointer' }}
        />

        <CardContent>
          {branch && <ReviewContainer userId={userData.id} employeeName={`${userData.firstName} ${userData.lastName}`} employeeImage={userData.image} branchId={branch.id} companyId={branch.companyId} openDialog={reviewDialogOpen} onDialogClose={() => setReviewDialogOpen(false)} />}
          <Typography gutterBottom variant="h5" component="div">
            {userData.firstName}
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            {branch?.name} - {roleLabel}
          </Typography>
        </CardContent>
      </CardActionArea>
      {
        currentUserIsOwnerOf(userData.companyId)
      
      && <CardActions>
        <Button 
          size="small" 
          color="primary" 
          component={Link}
          to={viewLinkTarget}
        >
          VIEW
        </Button>
      </CardActions>}
    </Card>
  );
});

